import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getZeroOption, type Option, getValidSelectValue } from '@/src/utils';
import {
    SimpleButton,
    SimpleSingleSelect,
    FilterIcon,
    ICON_COLORS,
    FilterBarContainer,
    ErrorMessage,
} from '@/src/ui';
import type { InitialOptions, FilterState, StandardOption } from '../models/filterDTOs';

interface FiltersProps {
    refreshTableData: (filters: FilterState) => void;
    filterState: FilterState;
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
}

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const NoStandardOption = getZeroOption('No standards found');

const questionGroupOptions = [
    { id: 0, name: 'All' },
    { id: 1, name: 'Regular Exams' },
    { id: 2, name: 'Practice' },
    { id: 3, name: 'Dynamic' },
];

// TODO: Does not load based on selected options on refresh call
const Filters: React.FC<FiltersProps> = ({ refreshTableData, filterState, setFilterState }) => {
    const [error, setError] = useState('');

    const [questionTypeMasterOptions, setQuestionTypeMasterOptions] = useState<Array<Option>>([
        LoadingOption,
    ]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Array<Option>>([
        LoadingOption,
    ]);
    const [standardOptions, setStandardOptions] = useState<Array<Option>>([LoadingOption]);
    const [standardData, setStandardData] = useState<Array<StandardOption>>([]);

    const [fieldsChanged, setFieldsChanged] = useState(false);

    const updateStandardOptions = (data: Array<StandardOption>, gradeLevelMasterId: number) => {
        // Reset filter if needed
        if (gradeLevelMasterId === AllOption.id) {
            setStandardOptions([AllOption]);
            setFilterState((prev) => ({
                ...prev,
                standardId: Number(prev.standardId) || Number(AllOption.id),
            }));
            return;
        }

        const newStandardOptions = data
            .filter((option) => {
                return option.gradeLevelMasterId === gradeLevelMasterId;
            })
            .map((option) => ({
                id: option.id,
                name: option.name,
            }));

        if (!newStandardOptions.length) {
            setStandardOptions([NoStandardOption]);
            setFilterState((prev) => ({ ...prev, standardId: Number(NoStandardOption.id) }));
            return;
        }

        setStandardOptions([AllOption, ...newStandardOptions]);
        setFilterState((prev) => {
            const valid = newStandardOptions.some(
                (opt) => Number(opt.id) === Number(prev.standardId)
            );
            return {
                ...prev,
                standardId: valid ? prev.standardId : Number(AllOption.id),
            };
        });
    };

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getQuestionTypeMaster=true&getGradeLevelMaster=true&getStandard=true'
                );
                const data = res.data;

                const newQuestionTypeMasterOptions = data.questionTypeMasterOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setQuestionTypeMasterOptions([AllOption, ...newQuestionTypeMasterOptions]);
                setFilterState((prev) => {
                    const valid = newQuestionTypeMasterOptions.some(
                        (opt) => Number(opt.id) === Number(prev.questionTypeMasterId)
                    );
                    return {
                        ...prev,
                        questionTypeMasterId: valid
                            ? prev.questionTypeMasterId
                            : Number(AllOption.id),
                    };
                });

                const newGradeLevelMasterOptions = data.gradeLevelMasterOptions;
                setGradeLevelMasterOptions([AllOption, ...newGradeLevelMasterOptions]);
                setFilterState((prev) => {
                    const valid = newGradeLevelMasterOptions.some(
                        (opt) => Number(opt.id) === Number(prev.gradeLevelMasterId)
                    );
                    return {
                        ...prev,
                        gradeLevelMasterId: valid ? prev.gradeLevelMasterId : Number(AllOption.id),
                    };
                });

                const newStandardOptions = data.standardOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setStandardOptions([AllOption, ...newStandardOptions]);
                setStandardData(newStandardOptions);
                setFilterState((prev) => {
                    const valid = newStandardOptions.some(
                        (opt) => Number(opt.id) === Number(prev.standardId)
                    );
                    return {
                        ...prev,
                        standardId: valid ? prev.standardId : Number(AllOption.id),
                    };
                });
                updateStandardOptions(newStandardOptions, filterState.gradeLevelMasterId);
            } catch {
                setError('Failed to fetch grade and district options.');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                justifyContent: 'center',
                mb: 2,
            }}
        >
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="question-type-select"
                    label="Question Type"
                    value={Number(
                        getValidSelectValue(
                            filterState.questionTypeMasterId,
                            questionTypeMasterOptions,
                            questionTypeMasterOptions[0]?.id ?? 0
                        )
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({
                            ...prev,
                            questionTypeMasterId: Number(e.target.value),
                        }));
                    }}
                    options={questionTypeMasterOptions}
                    error={error ? { message: error } : undefined}
                    sx={{ minWidth: 140, maxWidth: 280, height: 40, alignSelf: 'center' }}
                />
                <SimpleSingleSelect
                    name="grade-level-select"
                    label="Grade Level"
                    value={Number(
                        getValidSelectValue(
                            filterState.gradeLevelMasterId,
                            gradeLevelMasterOptions,
                            gradeLevelMasterOptions[0]?.id ?? 0
                        )
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGradeLevel = Number(e.target.value);
                        setFilterState((prev) => ({ ...prev, gradeLevelMasterId: newGradeLevel }));
                        updateStandardOptions(standardData, newGradeLevel);
                    }}
                    options={gradeLevelMasterOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="standard-select"
                    label="Standard"
                    value={Number(
                        getValidSelectValue(
                            filterState.standardId,
                            standardOptions,
                            standardOptions[0]?.id ?? 0
                        )
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, standardId: Number(e.target.value) }));
                    }}
                    options={standardOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="questionGroup"
                    label="Question Group"
                    value={Number(
                        getValidSelectValue(
                            filterState.questionGroupId,
                            questionGroupOptions,
                            questionGroupOptions[0]?.id ?? 0
                        )
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({
                            ...prev,
                            questionGroupId: Number(e.target.value),
                        }));
                    }}
                    options={questionGroupOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleButton
                    onClick={() => {
                        setFieldsChanged(false);
                        refreshTableData(filterState);
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ minHeight: 40, height: 40, alignSelf: 'center' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>
            <Box sx={{ display: 'flex', justifyContent: 'right' }}>
                <Box sx={{ minWidth: 240, maxWidth: 320 }}>
                    <ErrorMessage error={error} style={{ marginTop: 4 }} />
                </Box>
            </Box>
        </Box>
    );
};

export default Filters;
